import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { BrowserCodeReader, BrowserMultiFormatReader, IScannerControls } from "@zxing/browser";
import { NotFoundException, Result } from "@zxing/library";
import { i18nScope, provideClearlineCommonTranslocoScope } from "../../utils";
import { CommonModule } from "@angular/common";
import { TranslocoModule } from "@ngneat/transloco";

@Component({
  selector: "lib-qr-code-reader-modal",
  templateUrl: "./qr-code-reader-modal.component.html",
  styleUrls: ["./qr-code-reader-modal.component.scss"],
  standalone: true,
  imports: [CommonModule, TranslocoModule],
  providers: [provideClearlineCommonTranslocoScope()]
})
export class QrCodeReaderModalComponent implements OnInit {
  readonly i18nScope = i18nScope;

  private set deviceId(value: string) {
    this._deviceId = value;
  }

  private get deviceId(): string {
    return this._deviceId;
  }

  private set deviceLabel(value: string) {
    this._deviceName = value;
  }

  constructor(public activeModal: NgbActiveModal) {}

  _deviceId = "";
  _deviceName = "";

  @ViewChild("cameraStream") video: ElementRef<HTMLVideoElement> | null = null;

  showScan = false;
  showScanContainer = false;
  scanResult = "";
  error = "";

  private scannerControls: IScannerControls | null = null;
  private maxScanningTime = 15000;
  private numberOfRecognitions = 2;
  private scanningLoopInterval = 100;

  private lastScanningResult: Result | null = null;
  private stableScanningResult: Result | null = null;

  private timerId = 0;
  private stream: MediaStream | null = null;

  private static beep(): void {
    const snd = new Audio(
      "data:audio/wav;base64,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"
    );
    snd.play();
  }

  ngOnInit(): void {}

  public startScanning() {
    this.error = "";
    this.resetRecognitionResult();

    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then(
        (stream) => {
          this.stream = stream;
          this.switchRecognitionMode(true);
          return BrowserCodeReader.listVideoInputDevices();
        },
        (error) => {
          if (error.name === "NotFoundException") {
            this.error = "No valid video devices found";
          } else if (error.name === "NotAllowedError") {
            this.error = "Camera on your device is blocked";
          }

          console.error(typeof error);
          console.error(error);
          this.switchRecognitionMode(false);
          return [];
        }
      )
      .then((devices: MediaDeviceInfo[]) => {
        if (!devices.length) {
          this.error = "No video devices found";
          return;
        }
        return this.initScanning(devices[0]);
      })
      .then(
        (rs) => {
          console.log("scan chain worked");
        },
        (err) => {
          console.error(err);
          this.error = err.message;
        }
      );
  }

  initScanning(device: MediaDeviceInfo) {
    const self = this;

    clearInterval(this.timerId);
    this.recognitionLoop();
    this.reset();

    this.deviceId = device.deviceId;
    this.deviceLabel = device.label;
    const codeReader = new BrowserMultiFormatReader(); // new BrowserQRCodeReader();
    const previewElement = this.video?.nativeElement;
    return codeReader
      .decodeFromVideoDevice(this.deviceId, previewElement, (result, error, controls) => {
        self.lastScanningResult = result || null;
        if (error && !(error instanceof NotFoundException) && error.name !== "NotFoundException") {
          console.error(typeof error);
          console.error(error);
          this.error = error.message;
        }
        // use the result and error values to choose your actions
        // you can also use controls API in this scope like the controls
        // returned from the method.
      })
      .then((r) => {
        this.scannerControls = r;
      });
  }

  public stop(): void {
    this.reset();
    this.switchRecognitionMode(false);
  }
  private reset() {
    if (this.scannerControls != null) {
      this.scannerControls.stop();

      if (this.video) {
        this.video.nativeElement.srcObject = null;
      }
    }

    if (this.stream) {
      const tracks = this.stream.getTracks();
      tracks.forEach((track) => track.stop());
    }
  }

  private switchRecognitionMode(recognition: boolean): void {
    if (recognition) {
      this.showScan = false;
      this.showScanContainer = true;
    } else {
      this.showScan = true;
      this.showScanContainer = false;
    }
    this.displayRecognitionResult();
  }

  private recognitionLoop(): void {
    const self = this;
    let numberOfMatches = 0;
    // repeat with the interval and wait for scanning result
    this.timerId = setInterval(() => {
      if (self.lastScanningResult) {
        if (self.stableScanningResult && self.stableScanningResult.getText() === self.lastScanningResult.getText()) {
          numberOfMatches++;
          self.stableScanningResult = self.lastScanningResult;
          if (numberOfMatches >= self.numberOfRecognitions) {
            self.stop();
            clearInterval(this.timerId);
          }
        } else {
          QrCodeReaderModalComponent.beep();
          self.stableScanningResult = self.lastScanningResult;
          numberOfMatches = 1;
        }
      }
    }, this.scanningLoopInterval) as unknown as number;

    // close after 15 seconds
    setTimeout(() => {
      clearInterval(this.timerId);
      this.error = "Cannot recognize QR code";
      self.stop();
    }, self.maxScanningTime) as unknown as number;
  }

  public switchCamera() {
    BrowserCodeReader.listVideoInputDevices()
      .then((devices) => {
        let index = devices.findIndex((d) => d.deviceId === this.deviceId);
        index++;
        if (index === devices.length) index = 0;
        const device = devices[index];
        return this.initScanning(device);
      })
      .then(
        (res) => {
          console.log("camera switched");
        },
        (err) => {
          console.error(err);
          this.error = err.message;
        }
      );
  }

  private displayRecognitionResult(): void {
    if (this.stableScanningResult) {
      this.scanResult = this.stableScanningResult.getText();
      this.activeModal.close(this.scanResult);
    } else {
      this.scanResult = "";
    }
  }

  private resetRecognitionResult() {
    this.lastScanningResult = null;
    this.stableScanningResult = null;
    this.displayRecognitionResult();
  }

  close() {
    this.stop();
    this.activeModal.dismiss();
  }
}
